import { z } from 'zod'
import { type PatientChartListItem, patientChartListItemSchema } from './types'

const STORAGE_KEY = 'admin:patientCharts:recentlyViewed'
export const MAX_RECENTLY_VIEWED = 10

const recentlyViewedChartSchema = patientChartListItemSchema.extend({
  viewedAt: z.number(),
})

export type RecentlyViewedChart = z.infer<typeof recentlyViewedChartSchema>

const recentlyViewedListSchema = z.array(recentlyViewedChartSchema)

export function getRecentlyViewedCharts(): RecentlyViewedChart[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return []
    }

    const raw: unknown = JSON.parse(stored)
    const result = recentlyViewedListSchema.safeParse(raw)

    // Corrupt or outdated stored data recovers to an empty list.
    return result.success ? result.data : []
  } catch (error) {
    // localStorage may be unavailable (private mode / disabled storage).
    void error
    return []
  }
}

/**
 * Records a chart as most-recently viewed: prepends it, de-dupes by PatientId,
 * and caps the list at {@link MAX_RECENTLY_VIEWED}. Best-effort — silently
 * skips when the entry is malformed or storage is unavailable.
 */
export function addRecentlyViewedChart(chart: PatientChartListItem): void {
  const parsed = recentlyViewedChartSchema.safeParse({ ...chart, viewedAt: Date.now() })
  if (!parsed.success) {
    return
  }

  const withoutDuplicate = getRecentlyViewedCharts().filter((entry) => entry.PatientId !== parsed.data.PatientId)
  const next = [parsed.data, ...withoutDuplicate].slice(0, MAX_RECENTLY_VIEWED)

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch (error) {
    // localStorage may be unavailable or over quota — recents are best-effort.
    void error
  }
}
